const INSTALL_DISMISSED_AT_KEY = "wunmax_shortcut_prompt_dismissed_at"
const INSTALL_DISMISSED_FOREVER_KEY = "wunmax_shortcut_prompt_dismissed_forever"
const INSTALL_INSTALLED_KEY = "wunmax_shortcut_prompt_installed"
const DISMISS_COOLDOWN_DAYS = 7
const POPUP_DELAY_MS = 3000
const POPUP_ID = "wunmax-shortcut-popup"
const STYLE_ID = "wunmax-shortcut-popup-style"

let deferredInstallPrompt = null
let popupVisible = false

const isMobileDevice = () =>
	/Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent) || window.innerWidth <= 768

const isAndroidDevice = () => /Android/i.test(window.navigator.userAgent)

const isIosDevice = () => /iPhone|iPad|iPod/i.test(window.navigator.userAgent)

const isChromeBrowser = () => /Chrome|CriOS/i.test(window.navigator.userAgent)

const isSafariBrowser = () =>
	/Safari/i.test(window.navigator.userAgent) && !/Chrome|CriOS|Chromium|Android/i.test(window.navigator.userAgent)

const isSupportedMobileBrowser = () =>
	(isIosDevice() || isAndroidDevice()) && (isChromeBrowser() || isSafariBrowser())

const isStandalone = () =>
	window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true

const storage = {
	get(key) {
		try {
			return window.localStorage.getItem(key)
		} catch (error) {
			return null
		}
	},
	set(key, value) {
		try {
			window.localStorage.setItem(key, value)
		} catch (error) {
			// Ignore storage failures in strict/private browsing modes.
		}
	},
	remove(key) {
		try {
			window.localStorage.removeItem(key)
		} catch (error) {
			// Ignore storage failures in strict/private browsing modes.
		}
	},
}

const shouldDelayPrompt = () => {
	const dismissedAt = Number.parseInt(storage.get(INSTALL_DISMISSED_AT_KEY), 10)
	if (!dismissedAt) return false

	const cooldownMs = DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000
	return Date.now() - dismissedAt < cooldownMs
}

const shouldShowPrompt = () => {
	if (!isMobileDevice() || !isSupportedMobileBrowser() || isStandalone()) return false
	if (storage.get(INSTALL_INSTALLED_KEY) === "1") return false
	if (storage.get(INSTALL_DISMISSED_FOREVER_KEY) === "1") return false
	if (shouldDelayPrompt()) return false
	return true
}

const registerServiceWorker = async () => {
	if (!("serviceWorker" in navigator) || !window.isSecureContext) return

	try {
		await navigator.serviceWorker.register("/sw.js", { scope: "/" })
	} catch (error) {
		console.warn("Unable to register Wunmax shortcut service worker.", error)
	}
}

const injectStyles = () => {
	if (document.getElementById(STYLE_ID)) return

	const style = document.createElement("style")
	style.id = STYLE_ID
	style.textContent = `
		#${POPUP_ID} {
			position: fixed;
			inset: auto 0 1rem 0;
			z-index: 9999;
			display: flex;
			justify-content: center;
			padding: 0 1rem;
			pointer-events: none;
			opacity: 0;
			transform: translateY(24px);
			transition: opacity 0.25s ease, transform 0.25s ease;
		}

		#${POPUP_ID}.is-visible {
			opacity: 1;
			transform: translateY(0);
			pointer-events: auto;
		}

		#${POPUP_ID} .wunmax-shortcut-card {
			width: min(100%, 26rem);
			border-radius: 24px;
			padding: 1.1rem;
			color: #12302b;
			background:
				linear-gradient(160deg, rgba(255, 255, 255, 0.98), rgba(233, 250, 246, 0.98)),
				#ffffff;
			box-shadow: 0 24px 64px rgba(19, 74, 67, 0.22);
			border: 1px solid rgba(49, 151, 149, 0.18);
			backdrop-filter: blur(10px);
		}

		#${POPUP_ID} .wunmax-shortcut-top {
			display: flex;
			align-items: flex-start;
			gap: 0.85rem;
		}

		#${POPUP_ID} .wunmax-shortcut-icon {
			width: 3rem;
			height: 3rem;
			flex: 0 0 3rem;
			border-radius: 18px;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			background: linear-gradient(135deg, #319795, #1f6e63);
			color: #ffffff;
			font-size: 1.25rem;
			font-weight: 700;
		}

		#${POPUP_ID} .wunmax-shortcut-copy {
			flex: 1;
			min-width: 0;
		}

		#${POPUP_ID} .wunmax-shortcut-title {
			margin: 0;
			font-size: 1rem;
			font-weight: 700;
			line-height: 1.35;
		}

		#${POPUP_ID} .wunmax-shortcut-text {
			margin: 0.45rem 0 0;
			font-size: 0.9rem;
			line-height: 1.55;
			color: rgba(18, 48, 43, 0.84);
		}

		#${POPUP_ID} .wunmax-shortcut-actions {
			display: grid;
			grid-template-columns: 1fr;
			gap: 0.65rem;
			margin-top: 1rem;
		}

		#${POPUP_ID} .wunmax-shortcut-btn {
			border: 0;
			border-radius: 999px;
			padding: 0.9rem 1rem;
			font-size: 0.95rem;
			font-weight: 700;
			line-height: 1;
			cursor: pointer;
		}

		#${POPUP_ID} .wunmax-shortcut-btn-primary {
			background: linear-gradient(135deg, #319795, #236f69);
			color: #ffffff;
			box-shadow: 0 14px 30px rgba(49, 151, 149, 0.3);
		}

		#${POPUP_ID} .wunmax-shortcut-btn-secondary {
			background: rgba(49, 151, 149, 0.1);
			color: #14524d;
		}

		#${POPUP_ID} .wunmax-shortcut-dismiss {
			margin-top: 0.55rem;
			border: 0;
			background: transparent;
			color: rgba(18, 48, 43, 0.68);
			font-size: 0.82rem;
			text-decoration: underline;
			cursor: pointer;
		}

		@media (min-width: 769px) {
			#${POPUP_ID} {
				display: none;
			}
		}
	`
	document.head.appendChild(style)
}

const getPromptMarkup = () => {
	const canAutoPrompt = Boolean(deferredInstallPrompt)
	const title = canAutoPrompt
		? "Add Wunmax to your phone"
		: "Keep Wunmax one tap away"
	const text = canAutoPrompt
		? "Add a home screen shortcut for faster ordering. Your browser will ask you to confirm."
		: isIosDevice()
			? 'Open the Share menu, then tap "Add to Home Screen" to place this site on your phone.'
			: 'Open your browser menu and choose "Add to Home screen" to place this site on your phone.'
	const primaryLabel = canAutoPrompt ? "Add Shortcut" : "Okay"

	return `
		<div class="wunmax-shortcut-card" role="dialog" aria-modal="false" aria-labelledby="${POPUP_ID}-title">
			<div class="wunmax-shortcut-top">
				<div class="wunmax-shortcut-icon" aria-hidden="true">W</div>
				<div class="wunmax-shortcut-copy">
					<h3 class="wunmax-shortcut-title" id="${POPUP_ID}-title">${title}</h3>
					<p class="wunmax-shortcut-text">${text}</p>
				</div>
			</div>
			<div class="wunmax-shortcut-actions">
				<button type="button" class="wunmax-shortcut-btn wunmax-shortcut-btn-primary" data-action="primary">
					${primaryLabel}
				</button>
				<button type="button" class="wunmax-shortcut-btn wunmax-shortcut-btn-secondary" data-action="later">
					Maybe later
				</button>
			</div>
			<button type="button" class="wunmax-shortcut-dismiss" data-action="dismiss-forever">
				Don't show this again
			</button>
		</div>
	`
}

const hidePopup = ({ remember = true, forever = false } = {}) => {
	const popup = document.getElementById(POPUP_ID)
	if (!popup) return

	popup.classList.remove("is-visible")
	popupVisible = false

	if (forever) {
		storage.set(INSTALL_DISMISSED_FOREVER_KEY, "1")
		storage.remove(INSTALL_DISMISSED_AT_KEY)
		return
	}

	if (remember) {
		storage.set(INSTALL_DISMISSED_AT_KEY, String(Date.now()))
	}
}

const renderPopup = () => {
	injectStyles()

	let popup = document.getElementById(POPUP_ID)
	if (!popup) {
		popup = document.createElement("div")
		popup.id = POPUP_ID
		document.body.appendChild(popup)
	}

	popup.innerHTML = getPromptMarkup()
	const primaryButton = popup.querySelector('[data-action="primary"]')
	const laterButton = popup.querySelector('[data-action="later"]')
	const dismissForeverButton = popup.querySelector('[data-action="dismiss-forever"]')

	if (primaryButton) {
		primaryButton.addEventListener("click", onPrimaryAction)
	}

	if (laterButton) {
		laterButton.addEventListener("click", () => hidePopup())
	}

	if (dismissForeverButton) {
		dismissForeverButton.addEventListener("click", () =>
			hidePopup({ remember: false, forever: true })
		)
	}

	return popup
}

const showPopup = () => {
	if (!shouldShowPrompt()) return

	const popup = renderPopup()
	popup.classList.add("is-visible")
	popupVisible = true
}

async function onPrimaryAction() {
	if (!deferredInstallPrompt) {
		hidePopup()
		return
	}

	try {
		deferredInstallPrompt.prompt()
		const choice = await deferredInstallPrompt.userChoice

		if (choice && choice.outcome === "accepted") {
			storage.set(INSTALL_INSTALLED_KEY, "1")
			storage.remove(INSTALL_DISMISSED_AT_KEY)
			storage.remove(INSTALL_DISMISSED_FOREVER_KEY)
			deferredInstallPrompt = null
			hidePopup({ remember: false })
			return
		}
	} catch (error) {
		console.warn("Unable to open shortcut install prompt.", error)
	}

	deferredInstallPrompt = null
	if (popupVisible) {
		showPopup()
	} else {
		hidePopup()
	}
}

window.addEventListener("beforeinstallprompt", (event) => {
	event.preventDefault()
	deferredInstallPrompt = event

	if (shouldShowPrompt()) {
		showPopup()
	}
})

window.addEventListener("appinstalled", () => {
	storage.set(INSTALL_INSTALLED_KEY, "1")
	storage.remove(INSTALL_DISMISSED_AT_KEY)
	storage.remove(INSTALL_DISMISSED_FOREVER_KEY)
	hidePopup({ remember: false })
	deferredInstallPrompt = null
})

window.addEventListener("load", () => {
	registerServiceWorker()

	if (!shouldShowPrompt()) return

	window.setTimeout(() => {
		if (!shouldShowPrompt()) return
		showPopup()
	}, POPUP_DELAY_MS)
})
